import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { type ApiClient } from '../api/client';
import { type ZagruProblem } from '../api/problem';
import { useApiClient } from './ApiClientProvider';

export type ApiQueryOptions<T> = Omit<UseQueryOptions<T, ZagruProblem, T, readonly unknown[]>, 'queryKey' | 'queryFn'>;

/** `useQuery` bound to the nearest ApiClient; the abort signal is forwarded to the request. */
export function useApiQuery<T>(key: readonly unknown[], fn: (client: ApiClient, signal: AbortSignal) => Promise<T>, options?: ApiQueryOptions<T>): UseQueryResult<T, ZagruProblem> {
  const client = useApiClient();
  return useQuery<T, ZagruProblem, T, readonly unknown[]>({
    ...(options ?? {}),
    queryKey: key,
    queryFn: ({ signal }) => fn(client, signal),
  });
}

/** `useMutation` bound to the nearest ApiClient. */
export function useApiMutation<TData, TVars = void>(
  fn: (client: ApiClient, vars: TVars) => Promise<TData>,
  options?: UseMutationOptions<TData, ZagruProblem, TVars>,
): UseMutationResult<TData, ZagruProblem, TVars> {
  const client = useApiClient();
  return useMutation<TData, ZagruProblem, TVars>({
    ...(options ?? {}),
    mutationFn: (vars) => fn(client, vars),
  });
}
